import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { ProfileLink } from "@/components/ProfileDrawer";
import { formatDateDDMMYYYY, formatTimeWAT } from "@/lib/format";
import { toast } from "sonner";
import { Plus, X, Dumbbell, GraduationCap, Trash2 } from "lucide-react";

const empty = {
  title: "", type: "TRAINING", start_at: "", end_at: "",
  location: "Sirod HQ", description: "", assigned_guard_ids: [],
};

export const Trainings = () => {
  const [items, setItems] = useState([]);
  const [guards, setGuards] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    const r = await api.get("/trainings");
    setItems(r.data);
  }, []);

  useEffect(() => {
    load();
    api.get("/guards").then(r => setGuards(r.data));
  }, [load]);

  const activeGuards = useMemo(() => guards.filter(g => g.active), [guards]);

  const guardName = (id) => guards.find(g => g.id === id)?.full_name || "—";

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
      };
      await api.post("/trainings", payload);
      toast.success(`${form.type === "WORKOUT" ? "Workout" : "Training"} scheduled`);
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this session?")) return;
    await api.delete(`/trainings/${id}`);
    toast.success("Session removed");
    load();
  };

  const toggleGuard = (gid) => {
    const has = form.assigned_guard_ids.includes(gid);
    setForm({ ...form, assigned_guard_ids: has ? form.assigned_guard_ids.filter(x => x !== gid) : [...form.assigned_guard_ids, gid] });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-muted" style={{ fontSize: 14 }}>{items.length} sessions scheduled</div>
        <button className="btn-primary inline-flex items-center gap-2" onClick={() => setOpen(true)} data-testid="add-training">
          <Plus size={14} /> Schedule session
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {items.map(t => {
          const isWorkout = t.type === "WORKOUT";
          const Icon = isWorkout ? Dumbbell : GraduationCap;
          return (
            <div key={t.id} className="s-card" data-testid={`training-${t.id}`}>
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-start gap-3">
                  <div style={{ width: 36, height: 36, borderRadius: 8,
                                background: isWorkout ? "rgba(201, 168, 76, 0.15)" : "rgba(46,202,200,0.15)",
                                display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon size={18} color={isWorkout ? "#C9A84C" : "#2ECAC8"} />
                  </div>
                  <div>
                    <div style={{ fontSize: 15, color: "#fff" }}>{t.title}</div>
                    <div className="text-muted mono" style={{ fontSize: 12, marginTop: 4 }}>
                      {formatDateDDMMYYYY(t.start_at)} · {formatTimeWAT(t.start_at)} → {formatTimeWAT(t.end_at)}
                    </div>
                    <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>{t.location}</div>
                  </div>
                </div>
                <button onClick={() => remove(t.id)} className="text-alert" title="Delete"><Trash2 size={14} /></button>
              </div>
              {t.description && <div className="text-muted" style={{ fontSize: 13, marginBottom: 10 }}>{t.description}</div>}
              <div className="text-muted" style={{ fontSize: 11, marginBottom: 6 }}>
                {(t.assigned_guard_ids || []).length} assigned · {(t.completed_guard_ids || []).length} completed
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(t.assigned_guard_ids || []).slice(0, 6).map(gid => (
                  <ProfileLink key={gid} kind="guard" id={gid} className="badge badge-neutral">
                    {guardName(gid).split(" ")[0]}
                  </ProfileLink>
                ))}
                {(t.assigned_guard_ids || []).length > 6 && (
                  <span className="badge badge-neutral">+{(t.assigned_guard_ids || []).length - 6}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {open && (
        <div onClick={() => setOpen(false)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 100,
        }}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="s-card"
                style={{ width: 520, maxWidth: "95vw", maxHeight: "92vh", overflowY: "auto" }}>
            <div className="flex justify-between items-center mb-4">
              <div className="h-section">Schedule session</div>
              <button type="button" onClick={() => setOpen(false)} className="text-muted"><X size={18} /></button>
            </div>

            <div className="flex gap-2 mb-3">
              <button type="button" onClick={() => setForm({...form, type: "TRAINING"})}
                      className={form.type === "TRAINING" ? "btn-primary" : "btn-outline"} style={{ flex: 1 }} data-testid="type-training">
                Training
              </button>
              <button type="button" onClick={() => setForm({...form, type: "WORKOUT"})}
                      className={form.type === "WORKOUT" ? "btn-primary" : "btn-outline"} style={{ flex: 1 }} data-testid="type-workout">
                Workout
              </button>
            </div>

            <label className="text-muted" style={{ fontSize: 12 }}>Title</label>
            <input required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}
                   className="s-input mt-1 mb-3" placeholder={form.type === "WORKOUT" ? "Morning conditioning" : "Defensive tactics drill"} data-testid="t-title" />

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-muted" style={{ fontSize: 12 }}>Start</label>
                <input type="datetime-local" required value={form.start_at}
                       onChange={(e) => setForm({...form, start_at: e.target.value})} className="s-input mt-1" />
              </div>
              <div>
                <label className="text-muted" style={{ fontSize: 12 }}>End</label>
                <input type="datetime-local" required value={form.end_at}
                       onChange={(e) => setForm({...form, end_at: e.target.value})} className="s-input mt-1" />
              </div>
            </div>

            <label className="text-muted" style={{ fontSize: 12 }}>Location</label>
            <input required value={form.location} onChange={(e) => setForm({...form, location: e.target.value})}
                   className="s-input mt-1 mb-3" />

            <label className="text-muted" style={{ fontSize: 12 }}>Description</label>
            <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
                      className="s-input mt-1 mb-3" rows={2} />

            <label className="text-muted block" style={{ fontSize: 12, marginBottom: 8 }}>Assign guards</label>
            <div className="max-h-44 overflow-y-auto flex flex-wrap gap-2 mb-4 p-2 rounded-lg"
                 style={{ border: "1px solid var(--border-dark)" }}>
              {activeGuards.map(g => {
                const active = form.assigned_guard_ids.includes(g.id);
                return (
                  <button type="button" key={g.id} onClick={() => toggleGuard(g.id)}
                          className={active ? "btn-primary" : "btn-outline"}
                          style={{ padding: "5px 10px", fontSize: 11 }}>
                    {g.full_name}
                  </button>
                );
              })}
            </div>

            <button type="submit" className="btn-primary w-full" data-testid="t-submit">Schedule</button>
          </form>
        </div>
      )}
    </div>
  );
};
